"use client"

import { Search } from "lucide-react"

import { Button } from "@/components/ui/button"

interface ProductCatalogRecoveryProps {
  searchTerm: string
  categories: string[]
  onSearch: (query: string) => void
  onCategory: (category: string) => void
  onClear: () => void
}

const COMMON_NEEDS = ['Fertilizer', 'Grass seed', 'Weed control', 'Bermuda']

export function ProductCatalogRecovery({
  searchTerm,
  categories,
  onSearch,
  onCategory,
  onClear,
}: ProductCatalogRecoveryProps) {
  return (
    <div className="ds-glass rounded-2xl p-5 text-center md:p-8" role="status" aria-live="polite">
      <Search className="mx-auto mb-3 h-8 w-8 text-gray-500" />
      <h2 className="text-xl font-bold text-gray-900 md:text-2xl">
        No products found for &quot;{searchTerm}&quot;
      </h2>
      <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-gray-600 md:text-base">
        Try a lawn need or grass type, or open a category to start over.
      </p>
      <div className="mt-6 space-y-5 text-left">
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Try a common need</h3>
          <div className="flex flex-wrap gap-2">
            {COMMON_NEEDS.map((query) => (
              <button
                key={query}
                type="button"
                onClick={() => onSearch(query)}
                className="inline-flex min-h-[44px] items-center rounded-full border border-green-200 bg-white/75 px-4 py-2 text-sm font-semibold text-green-800 transition hover:border-green-300 hover:bg-green-50 focus:outline-hidden focus:ring-4 focus:ring-green-500/15"
              >
                {query}
              </button>
            ))}
          </div>
        </div>
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Browse a category</h3>
          <div className="flex flex-wrap gap-2">
            {categories.slice(0, 4).map((category) => (
              <button
                key={category}
                type="button"
                onClick={() => onCategory(category)}
                className="inline-flex min-h-[44px] items-center rounded-full border border-gray-200 bg-white/75 px-4 py-2 text-sm font-medium text-gray-800 transition hover:border-gray-300 hover:bg-white focus:outline-hidden focus:ring-4 focus:ring-green-500/15"
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>
      <Button
        onClick={onClear}
        variant="ghost"
        className="mt-6 min-h-[44px] text-green-700 hover:bg-green-50 hover:text-green-800"
      >
        Clear search and show all products
      </Button>
    </div>
  )
}
